import React, {useEffect, useState} from 'react';
import Portal from "@Features/portals/components/Portal";
import LazyLoading from "@Features/lazyLoading/components/LazyLoading";
import ReviewFilters from "./ReviewFilters";
import ReviewList from "./ReviewList";
import OverallScore from "./OverallScore";
import datasetParser from "@Dataset/datasetParser";
import ReviewPlaceholder from "./ReviewPlaceholder";
import Notification from "@Features/notification/components/Notification";
import usePrevious from "@Hooks/refs/usePrevious";
import useAsyncEffect from "@Hooks/promises/useAsyncEffect";
import stringifyObject from "@Object/stringifyObject";
import ajaxHandler from "@Ajax/v2/ajaxHandler";
import getInputFieldUpdateHandler from "@Pages/reviews/functions/getInputFieldUpdateHandler";
import getVerifiedHandler from "@Pages/reviews/functions/getVerifiedHandler";
import getArrayStateHandler from "@Pages/reviews/functions/getArrayStateHandler";
import getUserRatingHandler from "@Pages/reviews/functions/getUserRatingHandler";
import {initialFilters} from "@Pages/reviews/constants/initialFilters";
import {useTranslation} from "@Translation/hooks/useTranslation";
import FilteredScore from "@Pages/reviews/components/FilteredScore";
import {useScroll} from "@Scroll/hooks/useScroll";

function ReviewsPage() {
    const {
        reviewScoreBreakdown,
        reviews: reviewsInitialValue,
        overallScore,
        numberOfRatings,
        companySubscore,
        stations: stationsData,
        stationsIds: stationIdsInitialValue,
        company: companyInitialValue,
        leaveAReview,
        filteredNumberOfReviews,
        filteredRatingScore
    } = datasetParser({id: "company-review-react-portal"})

    let timeout = null

   const [reviews, setReviews] = useState(reviewsInitialValue)
    const [filters, setFilters] = useState({...initialFilters, stations: stationIdsInitialValue, company: companyInitialValue})
    const [page, setPage] = useState(1)
    const [loading, setLoading] = useState(false)
    const [batchLoading, setBatchLoading] = useState(false)
    const [scrollToTopVisible, setScrollToTopVisible] = useState(false)
    const [stations, setStations] = useState(stationsData)

    const handleInputFieldUpdate = getInputFieldUpdateHandler(setFilters)
    const verifiedHandler = getVerifiedHandler(setFilters)
    const arrayStateHandler = getArrayStateHandler(setFilters)
    const userRatingHandler = getUserRatingHandler(setFilters)

    const previousFilters = usePrevious(filters)
    const previousPage = usePrevious(page)

    const {elRef: scrollToRef, executeScroll} = useScroll({behavior: "instant", block: "start"})

    const{
        ratings,
        tags,
        languages,
        stations: selectedStations,
        verified,
        company: selectedCompany
    } = filters

    const isThereFilter = ratings.length === 0 && languages.length === 0 && selectedStations.length === 0  && tags.length === 0 && selectedCompany === '' && verified === 0

    //display filtered average score only if page is prefiltered, and no additional filters are active
    let shouldFilteredScoreBeDisplayed = ratings.length === 0 && languages.length === 0 && tags.length === 0 && verified === 1  && filters.company == companyInitialValue

    if (shouldFilteredScoreBeDisplayed) {
        filters.stations.map(stationId => {
            if (!stationIdsInitialValue.includes(stationId)) {
                shouldFilteredScoreBeDisplayed = false
            }
        })
        stationIdsInitialValue.map(stationId => {
            if (!filters.stations.includes(stationId)) {
                shouldFilteredScoreBeDisplayed = false
            }
        })
    }

    const noResultsWithFiltersMessage = useTranslation("global_review_page.no_results.with_filters")
    const noResultsWithoutFiltersMessage = useTranslation("global_review_page.no_results.without_filters")

    /**
     * Updates filters or gets next batch of data with existing filters
     */
   const updateReviewFilters = async (filters, updatePage = false) => {
        setLoading(!updatePage)
        setBatchLoading(updatePage)

        let numberOfCompanyReviews = 0;
        let numberOfStationReviews = 0;

        reviews.map(review => {
            if (review.isStationReview) {
                numberOfStationReviews = numberOfStationReviews + 1;
            }  else {
                numberOfCompanyReviews = numberOfCompanyReviews + 1;
            }
        })

        const params = {
            page,
            ...stringifyObject(filters),
            numberOfCompanyReviews: (!updatePage || page == 1) ? 0 : numberOfCompanyReviews,
            numberOfStationReviews: (!updatePage || page == 1) ? 0 : numberOfStationReviews
        }

        clearTimeout(timeout)
        timeout = setTimeout(async () => {
            const response = await ajaxHandler({url: '/ajax/get-review-data', handle: "log", params, type: 'get'})
            // eslint-disable-next-line no-unsafe-optional-chaining
            const data = response?.status === 200 ? response?.data : []
            const newReviews = updatePage ? [...reviews, ...data] : data ?? []
            setReviews(newReviews)
            setLoading(false)
            setBatchLoading(false)
            if (!updatePage) {
                setPage(1)
            }
        }, 1000)
    }

    useAsyncEffect(async () => {
        if (previousFilters !== filters) {
            await updateReviewFilters(filters)
        }

        if (previousPage !== page && reviews.length >= 15 && page !== 1) {
            await updateReviewFilters(filters, true)
        }
    }, [page, filters])

    useEffect(() => {
        if (stationIdsInitialValue.length != 0 || companyInitialValue != '') {
            executeScroll();
        }
    }, []);

    const clearAllFilters = () => {
       setFilters({...initialFilters, verified: 0});
    }

    return (
        <Portal id="company-review-react-portal">
            <OverallScore
                numberOfRatings={numberOfRatings}
                overallScore={overallScore}
                companySubscore={companySubscore}
                leaveAReviewLink={leaveAReview}
            />


                <ReviewFilters
                    stations={stations}
                    reviewScoreBreakdown={reviewScoreBreakdown}
                    handleInputFieldUpdate={handleInputFieldUpdate}
                    userRatingHandler={userRatingHandler}
                    activeStationHandler={(stations) => arrayStateHandler(stations, 'stations')}
                    stationHandler={(station) => setStations([...stations, station])}
                    tagHandler={(tags) => arrayStateHandler(tags, 'tags')}
                    languageHandler={(languages) => arrayStateHandler(languages, 'languages')}
                    activeFilters={filters}
                    verifiedHandler={verifiedHandler}
                    clearAllFilters={clearAllFilters}
                />



            <div ref={scrollToRef}>
                {filteredRatingScore != 0 && filteredNumberOfReviews != 0 && shouldFilteredScoreBeDisplayed && (
                    <FilteredScore
                        score={filteredRatingScore}
                        numberOfReviews={filteredNumberOfReviews}
                    />)
                }
            </div>

            <LazyLoading
                loading={loading}
                loaderIcon={<ReviewPlaceholder/>}
                noResultsNotification={
                    <Notification>
                        { isThereFilter ? (
                            <h4>{noResultsWithoutFiltersMessage}</h4>
                        ) : (
                            <h4>{noResultsWithFiltersMessage}</h4>
                        )}
                    </Notification>
                   }
                fetchedDataLength={reviews.length}
                batchLoading={batchLoading}
                page={page}
                setPage={setPage}
                setScroll={setScrollToTopVisible}
            >
                <ReviewList
                    reviews={reviews}
                    activeStationHandler={(stations) => arrayStateHandler(stations, 'stations')}
                    stationHandler={(station) => setStations([...stations, station])}
                    companyHandler={(company) => setFilters({...filters, company})}
                    filteredStations={selectedStations}
                />
            </LazyLoading>
        </Portal>
    );
}

export default ReviewsPage;